import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import { consola } from 'consola';
import apiCartInstance, { cartQuantityService } from '../api';
import { loadCartFromStorage, clearCartStorage, saveCartToStorage } from '../lib/cartPersistence'
import { remapGuestItemsToBranch } from '../lib/guestBranchMigration'
import { useInstitutionStore } from '~/entities/institution/@x/order';
import { useAuthState } from '~/entities/auth/@x/order';
import { useUserProfileStore } from '~/entities/user/@x/order';
import { resolveBranchAliases } from '~/entities/product/@x/order';
import { apiCombo } from '~/entities/combo/@x/order';
import { debounce, lockBodyScroll, unlockBodyScroll, useAnalytics } from '@shared/lib';
import { pushToast } from '@shared/ui/kit';

import type { CartState, CartItem, CartProductItem, CartComboItem } from '@/utils/types/cart';
import { DEBOUNCE_SAVE_DELAY } from '@/utils/constants/cart';
import { buildComboUniqueId, type ComboSelectionModifiers } from '@/utils/comboSelections';

// Logger от Nuxt (consola)
const logger = consola.withTag('cart-store');

// Константа для debounce API запросов (уменьшение нагрузки на сервер)
const DEBOUNCE_API_DELAY = 500;

/**
 * Уходил ли уже первый шаг воронки заказа в этом визите. Живёт вне стора: стор
 * пересоздаётся вместе с приложением, а событие должно быть одно на визит.
 */
let cartOpenTracked = false;

export const useCartStore = defineStore('cart', () => {
  const storeInstitution = useInstitutionStore();
  const { isLoggedIn } = useAuthState();
  const storeUserProfile = useUserProfileStore();

  // На клиенте сразу поднимаем гостевую корзину из localStorage (до nextTick initializeCart),
  // чтобы /order и BasketMobile не «залипали» на SSR-пустом состоянии до async-инициализации.
  const bootstrappedGuestItems = process.client ? loadCartFromStorage() : [];

  // State
  const state = ref<CartState>({
    items: bootstrappedGuestItems,
    selectedDish: null,
    isBasketOpen: false,
    selectedBranchId: bootstrappedGuestItems[0]?.branch_id ?? null,
    isLoading: false,
  });

  // Отдельное состояние для UI: предпочитаемый ID варианта при открытии группового товара
  /**
   * Идёт переезд корзины на другой филиал.
   *
   * Отдельный флаг, а не сравнение branchId: внутри переезда `selectedBranchId`
   * выставляется РАНЬШЕ, чем подтянется состав нового филиала, и в этот зазор
   * корзина выглядит «готовой», оставаясь с прежними позициями. Механика подарков
   * успевала в него влезть, не находила подарок под новым id и клала ЕЩЁ один —
   * количество росло на единицу за каждое переключение филиала.
   */
  const isMigratingBranch = ref(false);

  /**
   * Позиции, которые не переехали в выбранный филиал: здесь их не готовят.
   *
   * Сервер такие в корзину нового филиала не принимает, а корзина прежнего уже
   * очищена — раньше позиция просто исчезала, и человек молча недосчитывался
   * товара. Держим её на клиенте и показываем в корзине красной карточкой:
   * пусть человек сам решит — удалить или вернуться на прежний филиал, где
   * она снова станет обычной позицией (при следующем переезде мы пробуем её
   * перевезти вместе со всеми).
   */
  const strandedItems = ref<CartItem[]>([]);

  /**
   * Возвращает застрявшие позиции в состав корзины после каждой загрузки с сервера:
   * сервер о них не знает, а человек должен видеть их красной карточкой, пока сам
   * не удалит или не вернётся на филиал, где их готовят.
   */
  const mergeStrandedItems = (): void => {
    if (strandedItems.value.length === 0) return;
    const present = new Set(state.value.items.map((item) => String(item.id)));
    const missing = strandedItems.value.filter((item) => !present.has(String(item.id)));
    if (missing.length > 0) state.value.items = [...state.value.items, ...missing];
  };

  const preferredVariantId = ref<number | string | null>(null);

  // Debounced save для гостей
  const saveCartDebounced = debounce(() => {
    if (!isLoggedIn.value) {
      saveCartToStorage(state.value.items);
    }
  }, DEBOUNCE_SAVE_DELAY);

  // ========================================
  // 🔥 ОПТИМИЗАЦИЯ: Debounced API синхронизация
  // ========================================

  /**
   * Map для хранения pending обновлений количества товаров
   * Key: уникальный ключ товара (id_branchId_addons_excludes)
   * Value: { quantity, branchId, addons, excludes }
   */
  const pendingQuantityUpdates = new Map<string, {
    id: string;
    quantity: number;
    branchId: number;
    addons: string[];
    excludes: string[];
  }>();

  /**
   * Создает уникальный ключ для товара в корзине
   */
  const createCartItemKey = (
    id: string,
    branchId: number,
    addons: any[] = [],
    excludes: any[] = [],
  ): string => {
    const normAddons = (addons ?? []).map(String).sort();
    const normExcludes = (excludes ?? []).map(String).sort();
    return `${id}_${branchId}_${JSON.stringify(normAddons)}_${JSON.stringify(normExcludes)}`;
  };

  /**
   * Синхронизирует pending обновления с сервером (debounced)
   *
   * ✅ ИСПРАВЛЕНО: Использует CartQuantityService для правильной установки количества
   * Теперь вычисляет дельту между текущим и серверным количеством
   */
  const syncQuantityWithServer = debounce(async () => {
    // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
    // userId берется из токена на backend, локальная проверка не нужна
    if (!isLoggedIn.value || pendingQuantityUpdates.size === 0) {
      return;
    }

    logger.debug('🔄 Syncing pending quantity updates with server', {
      updatesCount: pendingQuantityUpdates.size,
    });

    // Копируем pending обновления и очищаем Map
    const updates = [...pendingQuantityUpdates.values()];
    pendingQuantityUpdates.clear();

    // Отправляем все обновления параллельно
    const syncPromises = updates.map(async (update) => {
      try {
        // 🔥 КРИТИЧНО: Проверяем, существует ли товар в корзине
        // Если товар был удален через removeFromCart, не синхронизируем его
        const currentItem = state.value.items.find(item =>
          createCartItemKey(item.id, item.branch_id!, item.addons, item.excludes) ===
          createCartItemKey(update.id, update.branchId, update.addons, update.excludes),
        );

        if (!currentItem) {
          logger.warn('⚠️ Item was removed, skipping sync', { id: update.id });
          return;
        }

        const targetQuantity = currentItem.quantity;

        // ✅ ИСПРАВЛЕНО: Получаем текущее количество на сервере
        const serverQuantity = await cartQuantityService.getServerQuantity({
          branchId: String(update.branchId),
          productId: String(update.id),
          addons: update.addons,
          excludes: update.excludes,
        });

        logger.debug('🔍 Server quantity check', {
          id: update.id,
          serverQuantity,
          targetQuantity,
          delta: targetQuantity - serverQuantity,
        });

        // ✅ ИСПРАВЛЕНО: Используем сервис для установки точного количества
        await cartQuantityService.setExactQuantity({
          productId: String(update.id),
          branchId: String(update.branchId),
          currentQuantity: serverQuantity,
          targetQuantity,
          addons: update.addons,
          excludes: update.excludes,
        });

        logger.success('✅ Quantity synced', {
          id: update.id,
          from: serverQuantity,
          to: targetQuantity,
        });
      } catch (error: any) {
        logger.error('❌ Failed to sync quantity', {
          id: update.id,
          error: error?.message || error,
        });

        // В случае ошибки, перезагружаем корзину с сервера
        if (state.value.selectedBranchId) {
          await loadCartFromServer(state.value.selectedBranchId);
        }
      }
    });

    await Promise.allSettled(syncPromises);
    logger.success('🎉 All quantity updates synced');
  }, DEBOUNCE_API_DELAY);

  const initializeCart = async () => {
    logger.info('🚀 Initializing cart...', {
      isLoggedIn: isLoggedIn.value,
      userId: storeUserProfile.getUserId,
      selectedBranch: storeInstitution.selectedBranch?.id,
    });

    // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie), а не getUserId
    // getUserId может быть null в момент инициализации, но появится после fetchUser
    if (isLoggedIn.value) {
      // Для авторизованного пользователя
      logger.info('✅ User is authenticated (token exists), proceeding with cart initialization');

      const savedCart = loadCartFromStorage();
      const branchId = savedCart[0]?.branch_id || storeInstitution.selectedBranch?.id;

      logger.debug('Cart initialization data', {
        savedCartLength: savedCart.length,
        savedCartBranchId: savedCart[0]?.branch_id,
        selectedBranchId: storeInstitution.selectedBranch?.id,
        finalBranchId: branchId,
      });

      if (!branchId) {
        logger.warn('⚠️ No branch ID available, cannot load cart');
        return;
      }

      if (savedCart.length > 0) {
        // Если есть локальная корзина - мерджим с серверной
        logger.info('📥 User is logged in with local cart, transferring to server...', {
          localCartItems: savedCart.length,
        });
        await transferCartToServer();
      } else {
        // Если локальная корзина пуста - просто загружаем с сервера
        logger.info('📥 User is logged in with empty local cart, loading from server...', {
          branchId,
        });
        await loadCartFromServer(branchId);
      }
    } else {
      // Для гостя загружаем из localStorage
      logger.info('👤 User is guest, loading from localStorage');

      const savedCart = loadCartFromStorage();
      if (savedCart.length > 0) {
        logger.info('💾 Loading cart from localStorage', {
          itemsCount: savedCart.length,
          items: savedCart.map(i => ({ id: i.id, name: i.name, qty: i.quantity })),
        });
        state.value.items = savedCart;
        state.value.selectedBranchId = savedCart[0].branch_id;
      } else {
        logger.info('📭 No saved cart in localStorage');
      }
    }

    logger.success('✅ Cart initialized successfully!', {
      itemsCount: state.value.items.length,
      hasItems: state.value.items.length > 0,
      selectedBranchId: state.value.selectedBranchId,
    });
  };

  const loadCartFromServer = async (branchId?: number) => {
    logger.debug('loadCartFromServer called', {
      branchId,
      isLoggedIn: isLoggedIn.value,
      userId: storeUserProfile.getUserId,
    });

    // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
    // userId берется из токена на backend, локальная проверка не нужна
    if (!isLoggedIn.value) {
      logger.warn('❌ Cannot load cart: user not authenticated');
      return;
    }

    state.value.isLoading = true;
    try {
      const branchToLoad = branchId || state.value.selectedBranchId;

      if (!branchToLoad) {
        logger.warn('❌ Cannot load cart: no branch ID');
        return;
      }

      logger.info('📡 Sending API request to load cart...', { branchId: branchToLoad });
      const cartData = await apiCartInstance.getCart(branchToLoad);

      logger.info('🔄 Loading cart from server...', {
        branchId: branchToLoad,
        productsCount: cartData?.products?.length,
        hasProducts: Array.isArray(cartData?.products),
        firstProduct: cartData?.products?.[0],
        rawCartData: cartData,
        allProducts: cartData?.products,
      });

      if (cartData?.products && Array.isArray(cartData.products) && cartData.products.length > 0) {
        state.value.items = cartData.products
          .filter((item: any) => {
            // ✅ Фильтруем undefined/null элементы
            if (!item) {
              logger.warn('⚠️ Skipping undefined/null item from cart', item);
              return false;
            }
            return true;
          })
          // eslint-disable-next-line sonarjs/cognitive-complexity -- нормализация позиций корзины из ответа API
          .map((item: any) => {
          // ✅ ИСПРАВЛЕНО: Определяем тип элемента по наличию специфичных полей
          // Если есть comboId или selections - это КОМБО
          const isCombo = item.comboId || (item.selections && Array.isArray(item.selections));
          const isProduct = item.productId || (!isCombo && item.id);

          if (isCombo) {
            // Комбо товар — реконструируем уникальный ID точно так же, как в addComboToCart и updateCartFromServerResponse,
            // чтобы recommendations API получал корректный "combo-{id}-{base64}" вместо числового DB-row id.
            const rawSelections = item.selections || item.comboSelections || [];
            const serverComboId = item.comboId || item.combo_id;

            const comboUniqueId =
              item.id && String(item.id).startsWith('combo-')
                ? String(item.id) // Сервер уже вернул готовый уникальный ID
                : buildComboUniqueId(serverComboId, rawSelections);

            logger.debug('📦 Loading combo item from server:', {
              rawId: item.id,
              comboUniqueId,
              comboId: serverComboId,
              name: item.name,
              selections: rawSelections,
            });

            return {
              id: comboUniqueId,
              itemType: 'combo',
              comboId: String(serverComboId),
              name: item.name,
              price: Number(item.price),
              quantity: Number(item.quantity),
              img: Array.isArray(item.img) ? item.img : [],
              branch_id: branchToLoad,
              comboSelections: rawSelections,
              base_price: item.base_price,
              discount_percent: item.discount_percent,
              // Недоступность набора считает сервер (у гостя — guestBranchMigration):
              // без этих полей набор, которого нет в филиале, выглядел в корзине обычным,
              // а состав рисовался заглушками «Товар 434».
              stop_list: item.stop_list === true,
              combo_slot_unavailable: item.combo_slot_unavailable === true,
            };
          } else if (isProduct) {
            // Обычный товар
            return {
              id: String(item.id),
              itemType: 'product',  // ✅ Обязательно устанавливаем itemType
              productId: String(item.productId || item.id),
              name: item.name,
              price: Number(item.price),
              quantity: Number(item.quantity),
              img: Array.isArray(item.img) ? item.img : [],
              branch_id: branchToLoad,
              category_id: item.category_id,
              addons: Array.isArray(item.addons) ? item.addons.map(Number) : [],
              excludes: Array.isArray(item.excludes) ? item.excludes.map(Number) : [],
              size: item.size,
            };
          } else {
            // Легаси формат (для обратной совместимости)
            logger.warn('⚠️ Unknown item format, treating as legacy product:', item);
            return {
              id: String(item.id),
              itemType: 'product',  // ✅ По умолчанию считаем продуктом
              productId: String(item.id),
              name: item.name,
              price: Number(item.price),
              quantity: Number(item.quantity),
              img: Array.isArray(item.img) ? item.img : [],
              branch_id: branchToLoad,
              category_id: item.category_id,
              addons: Array.isArray(item.addons) ? item.addons.map(Number) : [],
              excludes: Array.isArray(item.excludes) ? item.excludes.map(Number) : [],
              size: item.size,
            };
          }
        });  // Закрываем .map()

        state.value.selectedBranchId = branchToLoad;

        logger.success('✅ Cart loaded from server!', {
          itemsCount: state.value.items.length,
          branchId: branchToLoad,
          items: state.value.items.map(i => ({ id: i.id, name: i.name, qty: i.quantity })),
        });
        mergeStrandedItems();
      } else {
        logger.warn('⚠️ Cart is empty from server or invalid response', {
          branchId: branchToLoad,
          hasCartData: !!cartData,
          hasProducts: !!cartData?.products,
          isProductsArray: Array.isArray(cartData?.products),
          productsLength: cartData?.products?.length,
        });
        state.value.items = [];
        mergeStrandedItems();
      }
    } catch (error: any) {
      // Cart API не критично - не пробрасываем ошибку дальше
      logger.warn('Failed to load cart from server (non-critical)', error?.message || error);
    } finally {
      state.value.isLoading = false;
      logger.debug('📊 Cart state after load:', {
        itemsCount: state.value.items.length,
        isLoading: state.value.isLoading,
        selectedBranchId: state.value.selectedBranchId,
      });
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity -- перенос локальной корзины на сервер (много веток условий)
  const transferCartToServer = async () => {
    try {
      // 1. Проверка условий выполнения
      // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
      // userId берется из токена на backend, локальная проверка не нужна
      if (!process.client || !isLoggedIn.value) {
        logger.debug('transferCartToServer: skipping', {
          isClient: process.client,
          isLoggedIn: isLoggedIn.value,
        });
        return;
      }

      logger.info('📤 Starting cart transfer to server...');

      // 2. Загрузка из localStorage
      const savedCart = loadCartFromStorage();

      // 3. Получение branch_id
      const branchId = savedCart[0]?.branch_id || storeInstitution.selectedBranch?.id;
      if (!branchId) return;

      // 4. Получаем корзину с сервера
      const serverCart = await apiCartInstance.getCart(branchId).catch(error => {
        // Cart API не критично - возвращаем пустую корзину
        logger.warn('Failed to load cart during transfer (non-critical)', error?.message || error);
        return { products: [] };
      });

      // 5. Создаем карту серверных товаров с их addon/excludes (только для обычных товаров)
      const serverItemsMap = new Map(
        serverCart.products
          .filter((item: any) => item.itemType !== 'combo') // Исключаем комбо
          .map(item =>
            [
              JSON.stringify({ id: item.id, addons: item.addons ?? [], excludes: item.excludes ?? [] }),
              item.quantity,
            ],
          ),
      );

      // 5.1. Создаем карту серверных комбо (по comboId + selections)
      const serverCombosMap = new Map(
        serverCart.products
          .filter((item: any) => item.itemType === 'combo')
          .map((item: any) => {
            const key = buildComboUniqueId(
              item.comboId,
              item.comboSelections || item.selections || [],
            );
            return [key, item];
          }),
      );

      // 6. Подготовим обновленное состояние для Pinia
      const mergedItems: CartItem[] = serverCart.products.map((item: any) => {
        // ✅ Комбо: просто переносим с сервера (они уже там после transfer)
        if (item.itemType === 'combo') {
          return {
            id: item.id,
            itemType: 'combo',
            comboId: String(item.comboId),
            name: item.name,
            price: Number(item.price),
            quantity: Number(item.quantity),
            img: Array.isArray(item.img) ? item.img : [],
            branch_id: branchId,
            comboSelections: item.comboSelections || item.selections || [],
            base_price: item.base_price,
            discount_percent: item.discount_percent,
          } as any;
        }

        // ✅ Обычные товары: мерджим с локальной корзиной
        const localItem = savedCart.find(local =>
          (local as any).itemType !== 'combo' &&
          local.id === item.id &&
          JSON.stringify(local.addons ?? []) === JSON.stringify(item.addons ?? []) &&
          JSON.stringify(local.excludes ?? []) === JSON.stringify(item.excludes ?? []),
        );
        return {
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: localItem ? item.quantity + localItem.quantity : item.quantity,
          img: Array.isArray(item.img) ? item.img : [],
          branch_id: branchId,
          category_id: item.category_id,
          addons: Array.isArray(item.addons) ? item.addons.map(Number) : [],
          excludes: Array.isArray(item.excludes) ? item.excludes.map(Number) : [],
          size: item.size,
        };
      });

      // Добавляем товары, которые есть только в локальной корзине
      for (const localItem of savedCart) {
        // ⚠️ ПРОВЕРКА: Пропускаем старые комбо (с combo_id но без itemType)
        if ((localItem as any).combo_id && !(localItem as any).itemType) {
          logger.warn('⚠️ Skipping legacy combo item (not supported):', {
            id: localItem.id,
            name: localItem.name,
          });
          continue;
        }

        // ✅ Комбо: проверяем, есть ли уже на сервере
        if ((localItem as any).itemType === 'combo') {
          const comboItem = localItem as any;
          const key = buildComboUniqueId(comboItem.comboId, comboItem.comboSelections || []);

          // Если комбо еще нет на сервере, добавляем в merged items
          if (!serverCombosMap.has(key)) {
            logger.debug('📦 Adding local combo to merged items (will be transferred)', {
              comboId: comboItem.comboId,
              quantity: comboItem.quantity,
            });
            mergedItems.push({
              id: comboItem.id,
              itemType: 'combo',
              comboId: String(comboItem.comboId),
              name: comboItem.name,
              price: Number(comboItem.price),
              quantity: Number(comboItem.quantity),
              img: Array.isArray(comboItem.img) ? comboItem.img : [],
              branch_id: branchId,
              comboSelections: comboItem.comboSelections || [],
              base_price: comboItem.base_price,
              discount_percent: comboItem.discount_percent,
            } as any);
          }
          continue;
        }

        // ✅ Обычные товары: добавляем, если не на сервере
        const localKey = JSON.stringify({
          id: localItem.id,
          addons: localItem.addons ?? [],
          excludes: localItem.excludes ?? [],
        });
        if (!serverItemsMap.has(localKey)) {
          mergedItems.push({
            id: localItem.id,
            name: localItem.name,
            price: localItem.price,
            quantity: localItem.quantity,
            img: Array.isArray(localItem.img) ? localItem.img : [],
            category_id: localItem.category_id,
            branch_id: branchId,
            addons: Array.isArray(localItem.addons) ? localItem.addons : [],
            excludes: Array.isArray(localItem.excludes) ? localItem.excludes : [],
          } as CartItem);
        }
      }

      // 7. Обновляем состояние Pinia сразу (для мгновенной реактивности)
      logger.info('Setting merged items to state', {
        mergedItemsCount: mergedItems.length,
        mergedItems: mergedItems.map(i => ({ id: i.id, name: i.name, quantity: i.quantity })),
      });

      state.value.items = mergedItems;
      state.value.selectedBranchId = branchId;

      logger.success('State updated', {
        stateItemsCount: state.value.items.length,
        branchId: state.value.selectedBranchId,
      });

      // 8. Переносим товары на сервер — ПОСЛЕДОВАТЕЛЬНО, не Promise.all.
      // POST /cart на сервере — read-modify-write всей корзины: параллельные
      // добавления читали её до чужой записи и затирали друг друга, из переноса
      // выживала одна последняя позиция. Бэк теперь ретраит конфликт версий,
      // но источник лавины параллельных POST в один агрегат — этот перенос,
      // и очередь из N позиций здесь дешевле N-1 конфликтов там.
      const transferOne = async (item: (typeof savedCart)[number]) => {
        // ⚠️ ПРОВЕРКА: Пропускаем старые комбо (с combo_id но без itemType)
        if ((item as any).combo_id && !(item as any).itemType) {
          logger.warn('⚠️ Skipping legacy combo transfer:', {
            id: item.id,
            name: item.name,
          });
          return;
        }

        // ✅ КОМБО: Используем специальный API endpoint
        if ((item as any).itemType === 'combo') {
          const comboItem = item as any;
          logger.debug('📦 Transferring combo to server', {
            comboId: comboItem.comboId,
            quantity: comboItem.quantity,
            selections: comboItem.comboSelections,
          });

          try {
            await apiCartInstance.addComboToCart({
              combo_id: String(comboItem.comboId),
              branch_id: String(comboItem.branch_id),
              quantity: comboItem.quantity,
              selections: comboItem.comboSelections || [],
            });
            logger.success('✅ Combo transferred', { comboId: comboItem.comboId });
          } catch (error) {
            logger.error('❌ Failed to transfer combo', {
              comboId: comboItem.comboId,
              error,
            });
            throw error;
          }
          return;
        }

        // ✅ ОБЫЧНЫЕ ТОВАРЫ: Используем CartQuantityService
        const itemKey = JSON.stringify({
          id: item.id,
          addons: item.addons ?? [],
          excludes: item.excludes ?? [],
        });
        const serverQuantity = serverItemsMap.get(itemKey) || 0;
        const localQuantity = item.quantity;

        await cartQuantityService.syncItemDuringTransfer({
          productId: String(item.id),
          branchId: String(item.branch_id),
          localQuantity,
          serverQuantity,
          addons: (item.addons ?? []).map(String),
          excludes: (item.excludes ?? []).map(String),
        });
      };

      for (const item of savedCart) {
        await transferOne(item);
      }

      clearCartStorage();

      // ✅ Корзина уже синхронизирована (state.value.items = mergedItems на строке 310)
      // Повторная загрузка НЕ нужна - избегаем дублирующего getCart запроса
      logger.success('✅ Cart successfully transferred to server', {
        itemsCount: state.value.items.length,
        branchId,
      });
      return true;
    } catch (error) {
      logger.error('Transfer cart error', error);
      return false;
    }
  };

  // Watcher для отслеживания isLoggedIn
  watch(
    isLoggedIn,
    async (isLoggedIn, wasLoggedIn) => {
      logger.debug('🔐 Auth state changed', {
        isLoggedIn,
        wasLoggedIn,
        transition: `${wasLoggedIn} → ${isLoggedIn}`,
      });

      if (isLoggedIn && wasLoggedIn === false) {
        // Пользователь только что авторизовался - переносим корзину
        logger.info('👤 User logged in, transferring cart to server...');
        await transferCartToServer();
        logger.success('✅ Cart transfer completed');
      } else if (!isLoggedIn && wasLoggedIn === true) {
        // Пользователь разлогинился - очищаем корзину
        logger.info('👋 User logged out, clearing cart');
        await clearCart();
      } else {
        logger.debug('No action needed for this auth state transition');
      }
    },
  );

  /** Позиция-комбо: у неё нет карточки товара, только слоты */
  const isComboCartItem = (item: CartItem) =>
    (item as any).itemType === 'combo' || (item as any).combo_id !== undefined;

  /** Все карточки, которые нужно резолвить: сами позиции и содержимое слотов комбо */
  const collectResolvableIds = (items: CartItem[]): string[] => {
    const ids = items.filter((item) => !isComboCartItem(item)).map((item) => String(item.id));

    for (const item of items.filter(isComboCartItem)) {
      const selections = (item as any).comboSelections ?? (item as any).combo_selections ?? [];
      for (const selection of selections) {
        for (const id of selection?.product_ids ?? []) ids.push(String(id));
        for (const modifier of selection?.modifiers ?? []) {
          ids.push(String(modifier.product_id));
          for (const id of modifier.addons ?? []) ids.push(String(id));
          for (const id of modifier.excludes ?? []) ids.push(String(id));
        }
      }
    }

    return ids;
  };

  /**
   * Комбо нового филиала: нужны и для гарда подмены слота, и чтобы понять, что
   * комбо здесь вообще не продаётся. Сбой запроса не ломает переезд — вернём
   * undefined, и комбо останутся нетронутыми.
   */
  const loadBranchCombos = async (toBranchId: number) => {
    try {
      const response: any = await apiCombo.getCombosForBranch(toBranchId);
      // Клиент кладёт тело ответа в `data`, а сервер отдаёт `{ combos: [...] }` —
      // берём первый уровень, который реально оказался массивом.
      return [response, response?.data, response?.combos, response?.data?.combos].find((value) =>
        Array.isArray(value),
      );
    } catch (error) {
      logger.warn('Cart migration (гость): не удалось получить комбо филиала', error);
      
    }
  };

  /**
   * Перенос ГОСТЕВОЙ корзины. Сервера в этом пути нет, поэтому карточки на
   * «двойников» нового филиала подменяет клиент — тем же resolve-for-branch,
   * что используют избранное, «вы заказывали» и повтор заказа.
   */
  const migrateGuestCart = async (
    sourceItems: CartItem[],
    toBranchId: number,
    fromBranchId: number | null,
  ) => {
    const hasCombo = sourceItems.some(isComboCartItem);
    const [resolved, branchCombos] = await Promise.all([
      resolveBranchAliases(collectResolvableIds(sourceItems), toBranchId),
      hasCombo ? loadBranchCombos(toBranchId) : Promise.resolve(),
    ]);

    const migration = remapGuestItemsToBranch(sourceItems, resolved, toBranchId, branchCombos);

    state.value.items = migration.items;
    state.value.selectedBranchId = toBranchId;
    saveCartToStorage(state.value.items);

    if (migration.unavailableCombos.length > 0) {
      logger.warn('Cart migration (гость): комбо не продаётся в новом филиале', {
        toBranchId,
        combos: migration.unavailableCombos.map((item) => item.name),
      });
    }

    if (migration.combosNeedingSlotChange.length > 0) {
      logger.warn('Cart migration (гость): в наборе нужно заменить блюдо', {
        toBranchId,
        combos: migration.combosNeedingSlotChange.map((item) => item.name),
      });
    }

    if (migration.unresolved.length > 0) {
      logger.warn('Cart migration (гость): позиции без карточки в новом филиале', {
        fromBranchId,
        toBranchId,
        items: migration.unresolved.map((item) => item.name),
      });
    }

    return {
      migrated: migration.remapped,
      skipped: [...migration.unresolved, ...migration.unavailableCombos],
    };
  };

  /**
   * Переносит текущую корзину на другой филиал при СМЕНЕ выбранного филиала.
   *
   * Серверная корзина хранится по филиалу (getCart(branchId)). Раньше при смене
   * филиала мы просто грузили корзину нового филиала — и она оказывалась пустой
   * (например, при выборе delivery-адреса, который авто-роутер увёл на другой
   * филиал). Теперь позиции «переезжают»: переотправляем их под новый branch_id,
   * чистим корзину исходного филиала и подтягиваем авторитетное состояние.
   *
   * Позиции, недоступные в новом филиале (нет в его меню — products_branchs),
   * сервер отклонит при добавлении: такие позиции мы пропускаем и логируем
   * (заказать их из нового филиала всё равно нельзя).
   */

  const migrateCartToBranch = async (toBranchId: number) => {
    isMigratingBranch.value = true;
    try {
    // Снимок текущей корзины (всё, что видит пользователь) до мутаций state.
    // Застрявшие позиции здесь УЖЕ есть: mergeStrandedItems домешал их в состав.
    // Второй раз добавлять их нельзя — количество удвоится. Пометку снимаем:
    // в новом филиале блюдо может готовиться, и тогда позиция станет обычной.
    const sourceItems = state.value.items.map((i) => ({ ...i, stop_list: false })) as CartItem[];
    strandedItems.value = [];
    const fromBranchId = state.value.selectedBranchId ?? sourceItems[0]?.branch_id ?? null;

    // Нечего переносить — просто переключаемся на новый филиал.
    if (sourceItems.length === 0) {
      state.value.selectedBranchId = toBranchId;
      if (isLoggedIn.value) await loadCartFromServer(toBranchId);
      return { migrated: 0, skipped: [] as CartItem[] };
    }

    // Гость: серверной корзины нет — переносим локально (см. migrateGuestCart).
    if (!isLoggedIn.value) return await migrateGuestCart(sourceItems, toBranchId, fromBranchId);

    // Авторизованный: переотправляем позиции на сервер под новый филиал.
    const skipped: CartItem[] = [];
    for (const item of sourceItems) {
      try {
        if ((item as any).itemType === 'combo') {
          const combo = item as CartComboItem;
          await apiCartInstance.addComboToCart({
            combo_id: String(combo.comboId),
            branch_id: String(toBranchId),
            quantity: combo.quantity,
            selections: combo.comboSelections ?? [],
          });
        } else {
          const product = item as CartProductItem;
          await apiCartInstance.addToCart({
            product_id: String(product.productId ?? product.id),
            branch_id: String(toBranchId),
            quantity: product.quantity,
            addons: (product.addons ?? []).map(String),
            excludes: (product.excludes ?? []).map(String),
          });
        }
      } catch (error) {
        logger.warn('Cart migration: позиция недоступна в новом филиале, пропускаем', {
          id: item.id,
          name: item.name,
          error,
        });
        skipped.push(item);
      }
    }

    // Чистим корзину исходного филиала, чтобы она «переехала», а не задвоилась.
    if (fromBranchId != null && Number(fromBranchId) !== Number(toBranchId)) {
      try {
        await apiCartInstance.clearCart(fromBranchId);
      } catch (error) {
        logger.warn('Cart migration: не удалось очистить корзину исходного филиала', {
          fromBranchId,
          error,
        });
      }
    }

    // Позиции, которые здесь не готовят, НЕ выбрасываем: держим их в корзине
    // красной карточкой, чтобы человек сам решил — удалить или вернуться на
    // прежний филиал. Заполняем ДО загрузки: она домешивает их в состав.
    strandedItems.value = skipped.map((item) => ({
      ...item,
      branch_id: toBranchId,
      stop_list: true,
    })) as CartItem[];

    // Подтягиваем авторитетное состояние нового филиала.
    state.value.selectedBranchId = toBranchId;
    await loadCartFromServer(toBranchId);

    if (skipped.length) {
      logger.warn(
        `Cart migration: ${skipped.length} позиц. недоступны в новом филиале и не перенесены`,
        skipped.map((s) => s.name),
      );

      const names = skipped.map((item) => item.name).filter(Boolean);
      const shown = names.slice(0, 3).join(', ');
      const rest = names.length - Math.min(names.length, 3);
      pushToast({
        title:
          names.length === 1
            ? 'Товар недоступен в этом филиале'
            : 'Часть товаров недоступна в этом филиале',
        description: rest > 0 ? `${shown} и ещё ${rest}` : shown,
        color: 'warning',
        timeout: 8000,
      });
    }
    return { migrated: sourceItems.length - skipped.length, skipped };
    } finally {
      isMigratingBranch.value = false;
    }
  };

  // Синхронизация корзины при СМЕНЕ выбранного филиала.
  // Без этого «Изменить ресторан» / авто-роутинг доставки меняли отображение,
  // но операции с корзиной шли со старым branch_id, а корзина обнулялась.
  watch(
    () => storeInstitution.selectedBranch?.id,
    async (newBranchId, oldBranchId) => {
      // Игнорируем первичную установку и отсутствие смены
      if (!newBranchId || oldBranchId == null || newBranchId === oldBranchId) return;
      if (newBranchId === state.value.selectedBranchId) return;

      logger.info('🏪 Selected branch changed — migrating cart', {
        from: oldBranchId,
        to: newBranchId,
      });

      await migrateCartToBranch(newBranchId);
    },
  );

  // Инициализация (не вызываем автоматически, чтобы контролировать async)
  // Вместо этого вызываем явно после монтирования app
  if (process.client) {
    // Откладываем инициализацию до следующего тика,
    // чтобы все stores успели создаться
    nextTick(() => {
      initializeCart().catch(error => {
        logger.error('Failed to initialize cart', error);
      });
    });
  }

  // ========================================
  // CART ACTIONS (бывшие в cartActions.ts)
  // ========================================

  /**
   * Добавить товар в корзину
   */
  const addToCart = async (
    product: Omit<CartItem, 'quantity' | 'branch_id'> & { addons?: number[], excludes?: number[], quantity?: number },
    branchId: number,
  ) => {
    const selectedAddons = product.addons ?? [];
    const selectedExcludes = product.excludes ?? [];
    // ✅ Начальное количество (по умолчанию 1, но можно передать через product.quantity)
    const initialQuantity = product.quantity ?? 1;

    // ✅ НОРМАЛИЗАЦИЯ: Приводим к string[] для корректного сравнения
    const normAddons = selectedAddons.map(String).sort();
    const normExcludes = selectedExcludes.map(String).sort();

    // Ищем уже добавленный элемент с таким же товаром, филиалом, addons и excludes
    const existingIndex = state.value.items.findIndex(item =>
      String(item.id) === String(product.id) &&
      String(item.branch_id) === String(branchId) &&
      JSON.stringify((item.addons ?? []).map(String).sort()) === JSON.stringify(normAddons) &&
      JSON.stringify((item.excludes ?? []).map(String).sort()) === JSON.stringify(normExcludes),
    );

    if (existingIndex >= 0) {
      // Если уже есть — увеличиваем на initialQuantity (или на 1, если не указано)
      const item = state.value.items[existingIndex];
      const step = product.quantity ?? 1;
      return updateQuantity(item.id, item.quantity + step, branchId, selectedAddons, selectedExcludes);
    }

    // Новый элемент корзины
    const newItem: CartItem = {
      ...product,
      branch_id: branchId,
      quantity: initialQuantity,
      addons: selectedAddons,
      excludes: selectedExcludes,
    };

    // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
    // userId берется из токена на backend
    if (isLoggedIn.value) {
      try {
        await apiCartInstance.addToCart({
          product_id: String(product.id),
          branch_id: String(branchId),
          quantity: initialQuantity,
          addons: selectedAddons.map(String),
          excludes: selectedExcludes.map(String),
        });
        state.value.items.push(newItem);
        logger.success('Item added to cart', { id: product.id, branchId });
      } catch (error) {
        logger.error('Add to cart error', error);
        throw error;
      }
    } else {
      state.value.items.push(newItem);
      saveCartToStorage(state.value.items);
      saveCartDebounced();
      logger.success('Item added to cart (guest)', { id: product.id });
    }

    state.value.selectedBranchId = branchId;
  };

  /**
   * Добавить комбо-товар в корзину (НОВАЯ СТРУКТУРА API)
   * @param comboData - Данные комбо с selections
   */
  const addComboToCart = async (comboData: {
    combo_id: string | number;
    name: string;
    price: number;
    img?: string[];
    selections: Array<{
      group_id: string | number;
      product_ids: (string | number)[];
      /** Кастомизация слота: добавки/отбавки выбранного товара */
      modifiers?: ComboSelectionModifiers[];
    }> | Record<string | number, string | number>;  // ⚠️ Поддержка старого формата
    base_price?: number;
    discount_percent?: number;
  }) => {
    if (!comboData || !comboData.combo_id) {
      logger.error('Invalid combo data', comboData);
      throw new Error('Invalid combo data');
    }

    const branchId = state.value.selectedBranchId || storeInstitution.selectedBranch?.id;
    if (!branchId) {
      logger.error('No branch selected');
      throw new Error('No branch selected');
    }

    // ⚠️ ВАЖНО: Нормализуем selections (поддержка старого и нового форматов)
    let normalizedSelections: Array<{
      group_id: string | number;
      product_ids: (string | number)[];
      modifiers?: ComboSelectionModifiers[];
    }>;

    if (Array.isArray(comboData.selections)) {
      normalizedSelections = comboData.selections;
    } else {
      // Старый формат: { 1: 705, 2: 709 } -> новый формат
      logger.warn('⚠️ Received legacy selections format, converting...', comboData.selections);
      normalizedSelections = Object.entries(comboData.selections).map(([groupId, productId]) => ({
        group_id: groupId,
        product_ids: [productId],
      }));
      logger.debug('✅ Converted to new format:', normalizedSelections);
    }

    // Генерируем уникальный ID для комбо на основе выборов (включая добавки слотов)
    const uniqueId = buildComboUniqueId(comboData.combo_id, normalizedSelections);

    // Проверяем, есть ли уже такой комбо в корзине
    const existingIndex = state.value.items.findIndex(
      (item: any) => item.id === uniqueId && String(item.branch_id) === String(branchId),
    );

    if (existingIndex >= 0) {
      // Если уже есть — увеличиваем количество
      const item = state.value.items[existingIndex];
      return updateQuantity(item.id, item.quantity + 1, branchId);
    }

    // Новый элемент корзины для комбо (НОВАЯ СТРУКТУРА)
    const newItem: any = {
      id: uniqueId,
      itemType: 'combo',
      comboId: String(comboData.combo_id),
      name: comboData.name,
      price: comboData.price,
      img: comboData.img || [],
      branch_id: branchId,
      quantity: 1,
      comboSelections: normalizedSelections,  // ✅ Используем нормализованные selections
      base_price: comboData.base_price,
      discount_percent: comboData.discount_percent,
    };

    if (isLoggedIn.value) {
      try {
        // Отправляем комбо на сервер с новой структурой
        await apiCartInstance.addComboToCart({
          combo_id: String(comboData.combo_id),
          branch_id: String(branchId),
          quantity: 1,
          selections: normalizedSelections,  // ✅ Используем нормализованные selections
        });

        // ✅ ИСПРАВЛЕНО: Перезагружаем корзину с сервера вместо локального добавления
        logger.info('🔄 Reloading cart from server after combo addition...');
        await loadCartFromServer(branchId);
        logger.success('Combo added to cart', { combo_id: comboData.combo_id, branchId });
      } catch (error) {
        logger.error('Add combo to cart error', error);
        throw error;
      }
    } else {
      // Гостевой режим
      state.value.items.push(newItem);
      saveCartToStorage(state.value.items);
      saveCartDebounced();
      logger.success('Combo added to cart (guest)', { combo_id: comboData.combo_id });
    }

    state.value.selectedBranchId = branchId;
  };

  /**
   * Добавить собранный набор («Собрать набор») в корзину.
   * Сервер раскладывает набор в обычные товары — отдельной сущности в корзине нет.
   * POST /api/v2/cart/bundle
   */
  const addBundleToCart = async (payload: {
    branch_id: number;
    base_product_id: number;
    bundle_id: number;
    step_selections: Array<{ step_id: number; product_id: number }>;
  }) => {
    try {
      logger.info('🛒 addBundleToCart', payload);
      const response = await apiCartInstance.addBundleToCart(payload);
      if (response?.cart) {
        await updateCartFromServerResponse(response.cart);
        logger.success('✅ Bundle added, cart updated');
      }
    } catch (error) {
      logger.error('❌ addBundleToCart error', error);
      throw error;
    }
  };

  /**
   * Set для отслеживания товаров, которые в процессе удаления (защита от race conditions)
   */
  const itemsBeingRemoved = new Set<string>();

  /**
   * Удалить товар из корзины
   */
  const removeFromCart = async (
    id: string,
    branchId: number,
    addons: any[] = [],
    excludes: any[] = [],
  ): Promise<void> => {
    // ✅ НОРМАЛИЗАЦИЯ: Приводим addons и excludes к string[] для корректного сравнения
    const normAddons = (addons ?? []).map(String).sort();
    const normExcludes = (excludes ?? []).map(String).sort();

    const itemKey = createCartItemKey(id, branchId, normAddons, normExcludes);

    // Застрявшая позиция (её нет в этом филиале) живёт только на клиенте:
    // на сервере её нет, удалять там нечего — убираем из кармана и из состава.
    const strandedIndex = strandedItems.value.findIndex((item) => String(item.id) === String(id));
    if (strandedIndex !== -1) {
      strandedItems.value.splice(strandedIndex, 1);
      state.value.items = state.value.items.filter((item) => String(item.id) !== String(id));
      logger.debug('Удалена недоступная в филиале позиция', { id });
      return;
    }

    // 🔥 ЗАЩИТА: Если товар уже удаляется, игнорируем повторные вызовы
    if (itemsBeingRemoved.has(itemKey)) {
      logger.warn('⚠️ Item is already being removed, ignoring duplicate call', { itemKey });
      return;
    }

    logger.debug('removeFromCart called', { id, branchId, addons: normAddons, excludes: normExcludes });

    const index = state.value.items.findIndex(
      item =>
        String(item.id) === String(id) &&
        String(item.branch_id) === String(branchId) &&
        JSON.stringify((item.addons ?? []).map(String).sort()) === JSON.stringify(normAddons) &&
        JSON.stringify((item.excludes ?? []).map(String).sort()) === JSON.stringify(normExcludes),
    );

    if (index === -1) {
      logger.warn('Item not found in cart', {
        id,
        branchId,
        addons: normAddons,
        excludes: normExcludes,
      });
      return;
    }

    const removedItem = state.value.items[index];

    // 🔥 КРИТИЧНО: Очищаем pending updates и отменяем debounced sync
    if (pendingQuantityUpdates.has(itemKey)) {
      pendingQuantityUpdates.delete(itemKey);
      logger.debug('🗑️ Cleared pending updates for removed item', { itemKey });
    }

    // 🔥 КРИТИЧНО: Отменяем debounced синхронизацию чтобы не отправился POST после DELETE
    syncQuantityWithServer.cancel();
    logger.debug('⏹️ Cancelled pending sync');

    // Помечаем товар как "удаляется"
    itemsBeingRemoved.add(itemKey);

    try {
      // 🔥 ОПТИМИЗАЦИЯ: Сразу удаляем из UI (оптимистичное удаление)
      state.value.items.splice(index, 1);
      logger.success('✨ UI updated - item removed optimistically', { id });

      // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
      // userId берется из токена на backend
      if (isLoggedIn.value) {
        await apiCartInstance.removeFromCart({
          product_id: String(id),
          branch_id: String(branchId),
          addons: normAddons,
          excludes: normExcludes,
        });
        logger.success('✅ Item removed from server', { id, branchId });
      } else {
        saveCartToStorage(state.value.items);
      }
    } catch (error: any) {
      logger.error('❌ removeFromCart error', error);

      // 🔥 ОТКАТ: Восстанавливаем товар в UI при ошибке
      state.value.items.splice(index, 0, removedItem);

      // Если 404 - товар уже удален на сервере, это не ошибка
      if (error?.response?.status === 404) {
        logger.warn('⚠️ Item already removed on server (404), keeping UI updated');
        state.value.items.splice(index, 1);
      } else {
        throw error;
      }
    } finally {
      // Убираем из "удаляется"
      itemsBeingRemoved.delete(itemKey);
    }
  };

  /**
   * Обновить количество товара в корзине (ОПТИМИСТИЧНО - сразу обновляет UI)
   *
   * 🔥 ОПТИМИЗАЦИЯ: Локальное обновление + debounced синхронизация с сервером
   * ✅ ИСПРАВЛЕНО: Использует CartQuantityService для правильной синхронизации
   *
   * @param id - ID товара
   * @param quantity - НОВОЕ абсолютное количество (не дельта!)
   * @param branchId - ID филиала
   * @param addons - список ID добавок
   * @param excludes - список ID исключений
   *
   * @example
   * // Было: 2, стало: 5
   * await updateQuantity('123', 5, 1)
   * // Внутри будет вычислена дельта +3 и отправлена на сервер через increaseQuantity(3)
   */
  const updateQuantity = async (
    id: string,
    quantity: number,
    branchId: number,
    addons: any[] = [],
    excludes: any[] = [],
  ): Promise<void> => {
    logger.debug('updateQuantity called', { id, quantity, branchId, addons, excludes });

    // ✅ Нормализация типов
    const normAddons = (addons ?? []).map(String).sort();
    const normExcludes = (excludes ?? []).map(String).sort();

    // ✅ Поиск товара в корзине
    // Для комбо-товаров проверяем только id и branch_id (addons/excludes не используются)
    const index = state.value.items.findIndex(
      item => {
        const idMatch = String(item.id) === String(id);
        const branchMatch = String(item.branch_id) === String(branchId);

        // Если это комбо - проверяем только id и branch_id
        if (item.itemType === 'combo') {
          return idMatch && branchMatch;
        }

        // Для обычных товаров проверяем также addons и excludes
        const addonsMatch = JSON.stringify((item.addons ?? []).map(String).sort()) === JSON.stringify(normAddons);
        const excludesMatch = JSON.stringify((item.excludes ?? []).map(String).sort()) === JSON.stringify(normExcludes);
        return idMatch && branchMatch && addonsMatch && excludesMatch;
      },
    );

    if (index === -1) {
      logger.warn('Item not found in cart for updateQuantity', {
        id,
        branchId,
        addons: normAddons,
        excludes: normExcludes,
      });
      return;
    }

    const currentItem = state.value.items[index];
    const currentQuantity = currentItem.quantity;

    logger.debug('Quantity change', { currentQuantity, newQuantity: quantity });

    // ✅ Если количество <= 0, удаляем товар
    if (quantity <= 0) {
      logger.debug('Quantity <= 0, removing item');
      await removeFromCart(id, branchId, normAddons.map(Number), normExcludes.map(Number));
      return;
    }

    // ✅ ИСПРАВЛЕНО: Используем переданное значение как есть (поддерживаем дробные значения)
    // Убрали Math.max(1, quantity) чтобы поддерживать шаг 0.25 и другие дробные значения
    const newQuantity = quantity;

    // 🔥 ОПТИМИЗАЦИЯ: Сразу обновляем UI (оптимистичное обновление)
    state.value.items[index] = {
      ...currentItem,
      quantity: newQuantity,
    };

    logger.success('✨ UI updated optimistically', { id, quantity: newQuantity });

    // ✅ Для гостей - сохраняем в localStorage
    if (!isLoggedIn.value) {
      saveCartToStorage(state.value.items);
      saveCartDebounced();
      return;
    }

    // ✅ Для авторизованных - добавляем в pending обновления
    // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
    // userId берется из токена на backend
    if (isLoggedIn.value) {
      const itemKey = createCartItemKey(id, branchId, normAddons, normExcludes);

      pendingQuantityUpdates.set(itemKey, {
        id: String(id),
        quantity: newQuantity,
        branchId,
        addons: normAddons,
        excludes: normExcludes,
      });

      logger.debug('📝 Added to pending updates', { itemKey, quantity: newQuantity });

      // 🔥 Вызываем debounced синхронизацию (запрос отправится через 500ms)
      syncQuantityWithServer();
    }
  };

  /**
   * Очистить корзину.
   *
   * Корзина живёт в трёх местах, и «очистить» значит убить её во всех: локальный state,
   * копия в localStorage и серверные корзины (они хранятся ПО ФИЛИАЛАМ). Пропустить любое
   * из них — значит получить «очистил, обновил страницу, товары вернулись»:
   *  — отложенный синк количеств уходит через debounce уже ПОСЛЕ очистки и пересоздаёт
   *    позиции на сервере;
   *  — localStorage у авторизованного не чистился, а initializeCart на следующем заходе
   *    переливает локальную корзину обратно на сервер;
   *  — при пустом selectedBranchId запрос на сервер просто не уходил, и корзина
   *    оставалась там нетронутой.
   */
  const clearCart = async () => {
    strandedItems.value = [];
    // 1. Гасим отложенные записи ДО удаления — иначе они догонят пустую корзину.
    pendingQuantityUpdates.clear();
    syncQuantityWithServer.cancel();
    saveCartDebounced.cancel();

    // 2. Филиалы, чьи корзины надо снести: выбранный + все, что остались в позициях
    // (после переключения ресторана в корзине могут лежать позиции прошлого филиала).
    // Ключи строкой: branch_id позиции может прийти числом или строкой, и Set из
    // разнотипных значений дал бы два одинаковых DELETE.
    const branchIds = new Set<string>();
    if (state.value.selectedBranchId != null) branchIds.add(String(state.value.selectedBranchId));
    for (const item of state.value.items) {
      if (item.branch_id != null) branchIds.add(String(item.branch_id));
    }
    if (branchIds.size === 0 && storeInstitution.selectedBranch?.id) {
      branchIds.add(String(storeInstitution.selectedBranch.id));
    }

    logger.info('Clearing cart', { branchIds: [...branchIds] });

    try {
      // ✅ ВАЖНО: Проверяем только isLoggedIn (токен из cookie)
      // userId берется из токена на backend
      if (isLoggedIn.value) {
        for (const branchId of branchIds) {
          await apiCartInstance.clearCart(branchId);
        }
      }

      // 3. Локальное состояние чистим только после успеха сервера: иначе экран покажет
      // пустую корзину, которой на сервере нет, и она вернётся при следующей загрузке.
      state.value.items = [];

      // Филиал НЕ сбрасываем: он выбран пользователем, а не корзиной. Со сброшенным
      // филиалом следующая очистка/загрузка не знала бы, куда идти.

      // 4. Локальная копия должна умереть в любом случае. У авторизованного она остаётся
      // от гостевой сессии, и initializeCart перельёт её на сервер при следующем заходе.
      clearCartStorage();

      logger.success('Cart cleared successfully');
    } catch (error) {
      logger.error('clearCart error', error);
      throw error;
    }
  };

  /**
   * Обновить локальную корзину из ответа сервера
   * Используется после increase/decrease API для синхронизации
   */
  const updateCartFromServerResponse = (serverCart: any) => {
    logger.info('🔄 updateCartFromServerResponse CALLED', {
      serverCart,
      hasItems: !!serverCart?.items,
      hasProducts: !!serverCart?.products,
      itemsCount: serverCart?.items?.length || 0,
    });

    if (!serverCart) {
      logger.warn('⚠️ Server cart is empty or undefined');
      return;
    }

    // ✅ ИСПРАВЛЕНО: Сервер возвращает массив items (не products!)
    const itemsArray = serverCart.items || serverCart.products;

    if (!Array.isArray(itemsArray)) {
      logger.error('❌ Server response does not contain items or products array', {
        serverCart,
        hasItems: !!serverCart.items,
        hasProducts: !!serverCart.products,
        itemsType: typeof serverCart.items,
        productsType: typeof serverCart.products,
      });
      return;
    }

    // ✅ КРИТИЧНО: Логируем RAW данные с сервера
    logger.info('🔍🔍🔍 RAW server cart data 🔍🔍🔍', {
      itemsCount: itemsArray.length,
      rawItems: itemsArray.map((item: any) => ({
        // Все возможные поля для комбо
        comboId: item.comboId,
        combo_id: item.combo_id,
        item_type: item.item_type,
        // Все возможные поля для обычного товара
        productId: item.productId,
        product_id: item.product_id,
        id: item.id,
        // Общие поля
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        // Selections
        selections: item.selections,
        combo_selections: item.combo_selections,
        // Все ключи объекта
        allKeys: Object.keys(item),
      })),
    });

    logger.info('📦📦📦 Processing items from server 📦📦📦', {
      itemsCount: itemsArray.length,
      items: itemsArray.map((item: any) => ({
        productId: item.productId || item.id,
        comboId: item.comboId,
        item_type: item.item_type,
        quantity: item.quantity,
        name: item.name,
        hasSelections: !!(item.combo_selections || item.selections),
        selectionsLength: Array.isArray(item.combo_selections || item.selections) ? (item.combo_selections || item.selections).length : 0,
      })),
    });

    // eslint-disable-next-line sonarjs/cognitive-complexity -- разбор позиций заказа с сервера (комбо / обычный товар)
    state.value.items = itemsArray.map((item: any) => {
      // ✅ КРИТИЧНО: Логируем каждый элемент ДО проверки типа
      logger.info('🔍🔍🔍 Processing item from server 🔍🔍🔍', {
        item,
        hasItemType: !!item.item_type,
        itemType: item.item_type,
        hasComboId: !!item.comboId,
        comboId: item.comboId,
        hasCombo_id: !!item.combo_id,
        combo_id: item.combo_id,
        hasProductId: !!item.productId,
        productId: item.productId,
        hasSelections: !!(item.combo_selections || item.selections),
        selections: item.combo_selections || item.selections,
        allKeys: Object.keys(item),
      });

      // Проверяем тип товара (обычный или комбо)
      // ✅ КРИТИЧНО: Сервер может возвращать comboId (с заглавной) или combo_id (с подчеркиванием)
      const isCombo = item.item_type === 'combo' || !!item.combo_id || !!item.comboId;

      logger.info('🔍🔍🔍 Item type check 🔍🔍🔍', {
        isCombo,
        itemType: item.item_type,
        hasComboId: !!item.comboId,
        comboId: item.comboId,
        hasCombo_id: !!item.combo_id,
        combo_id: item.combo_id,
        hasProductId: !!item.productId,
        productId: item.productId,
        willProcessAsCombo: isCombo,
      });

      if (isCombo) {
        // Комбо товар
        // ✅ КРИТИЧНО: Нормализуем branch_id в число для правильного сравнения
        const branchIdValue = item.branch_id || item.branchId || serverCart.branchId;
        const normalizedBranchId = branchIdValue ? Number(branchIdValue) : null;

        // ✅ КРИТИЧНО: Генерируем ID так же, как в addComboToCart и getComboUniqueId
        // Нормализуем selections в тот же формат для совпадения ID
        const rawSelections = item.combo_selections || item.selections || [];
        // ✅ КРИТИЧНО: Сервер может возвращать comboId (с заглавной) или combo_id (с подчеркиванием)
        const serverComboId = item.comboId || item.combo_id;
        let comboId: string;

        if (item.id && item.id.startsWith('combo-')) {
          // Если сервер вернул ID комбо (уже сгенерированный), используем его
          comboId = item.id;
          logger.debug('🔑 Using server-provided combo ID', { comboId });
        } else {
          comboId = buildComboUniqueId(serverComboId, rawSelections);
          logger.info('🔑 Generated combo ID from server data', {
            comboIdFromServer: serverComboId,
            rawSelections,
            generatedId: comboId,
          });
        }

        const mappedCombo = {
          id: comboId,
          itemType: 'combo',
          comboId: String(serverComboId),
          name: item.name,
          price: item.price || item.final_price || 0,
          quantity: item.quantity || 1,
          branch_id: normalizedBranchId, // ✅ Всегда число!
          img: Array.isArray(item.img) ? item.img : (item.img ? [item.img] : []),
          comboSelections: rawSelections, // ✅ Сохраняем оригинальные selections
          base_price: item.base_price,
          discount_percent: item.discount_percent,
        };

        logger.info('✅✅✅ Mapped combo item from server ✅✅✅', {
          id: mappedCombo.id,
          comboId: mappedCombo.comboId,
          name: mappedCombo.name,
          quantity: mappedCombo.quantity,
          branch_id: mappedCombo.branch_id,
          branchIdType: typeof mappedCombo.branch_id,
          originalBranchId: branchIdValue,
          normalizedBranchId,
          rawSelections,
          comboSelections: mappedCombo.comboSelections,
          selectionsLength: Array.isArray(mappedCombo.comboSelections) ? mappedCombo.comboSelections.length : 0,
        });

        return mappedCombo;
      } else {
        // ✅ ИСПРАВЛЕНО: Обычный товар - используем productId вместо id
        // ✅ КРИТИЧНО: Нормализуем branch_id в число для правильного сравнения
        const branchIdValue = item.branch_id || item.branchId || serverCart.branchId;
        const normalizedBranchId = branchIdValue ? Number(branchIdValue) : null;

        const mappedProduct = {
          // id — это ID позиции в корзине (уникальный среди items)
          id: String(item.id || item.productId || item.product_id),
          name: item.name,
          price: item.price || 0,
          quantity: item.quantity || 1,
          branch_id: normalizedBranchId, // ✅ Всегда число!
          addons: item.addons || [],
          excludes: item.excludes || [],
          // ✅ ИСПРАВЛЕНО: img может быть объектом или массивом
          img: Array.isArray(item.img) ? item.img : (item.img ? [item.img] : []),
          category_id: item.category_id || item.categoryId,
          // ЯВНО сохраняем productId для последующего поиска товара в каталоге
          productId: String(item.productId || item.product_id || item.id),
        };
        logger.debug('✅ Mapped product item', {
          ...mappedProduct,
          originalBranchId: branchIdValue,
          normalizedBranchId,
        });
        return mappedProduct;
      }
    });

    // ✅ ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ всех элементов корзины
    const comboItems = state.value.items.filter((i: any) => i.itemType === 'combo');
    const productItems = state.value.items.filter((i: any) => i.itemType !== 'combo');

    logger.success('✅✅✅ Cart updated from server ✅✅✅', {
      itemsCount: state.value.items.length,
      comboItemsCount: comboItems.length,
      productItemsCount: productItems.length,
      allItems: state.value.items.map((item: any) => ({
        id: item.id,
        itemType: item.itemType,
        quantity: item.quantity,
        name: item.name,
        comboId: item.comboId,
        branch_id: item.branch_id,
        branchIdType: typeof item.branch_id,
        hasComboSelections: !!(item.comboSelections || item.selections),
        comboSelectionsLength: Array.isArray(item.comboSelections || item.selections) ? (item.comboSelections || item.selections).length : 0,
      })),
      comboItems: comboItems.map((item: any) => ({
        id: item.id,
        comboId: item.comboId,
        branch_id: item.branch_id,
        branchIdType: typeof item.branch_id,
        quantity: item.quantity,
        name: item.name,
        comboSelections: item.comboSelections || item.selections,
        comboSelectionsType: Array.isArray(item.comboSelections || item.selections) ? 'array' : typeof (item.comboSelections || item.selections),
      })),
    });

    // Сохраняем в localStorage для гостей
    if (!isLoggedIn.value) {
      saveCartToStorage(state.value.items);
      logger.debug('💾 Cart saved to localStorage');
    }

    // Принудительно триггерим реактивность
    logger.debug('🔄 Triggering Vue reactivity...');
  };

  /**
   * Первый шаг воронки заказа — «корзина перед глазами».
   *
   * Шлётся один раз за визит: на мобильных корзина открывается шторкой, а на сайте
   * она постоянная колонка сбоку, и открывать её нечем. Пока событие висело только
   * на шторке, вся воронка заказа на десктопе была пустой — при живых заказах.
   */
  const trackCartOpened = (): void => {
    if (!process.client || cartOpenTracked) return;
    cartOpenTracked = true;

    // Читаем состояние напрямую: `cart` и `totalPrice` — поля возвращаемого объекта
    // стора, а не переменные этой области. Прежний код обращался к ним по имени и
    // падал с ReferenceError, из-за чего событие не уходило ни разу.
    const items = state.value.items;

    useAnalytics().track('cart_open', {
      itemsCount: items.length,
      sum: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    });
  };

  /**
   * Открыть корзину
   */
  const openBasket = (): void => {
    // Гард по переходу состояния: lock/unlock считаются по ссылкам,
    // повторный open без close утёк бы блокировкой.
    if (state.value.isBasketOpen) return;
    state.value.isBasketOpen = true;
    lockBodyScroll();
    logger.debug('basket opened');

    trackCartOpened();
  };

  /**
   * Закрыть корзину
   */
  const closeBasket = (): void => {
    if (!state.value.isBasketOpen) return;
    state.value.isBasketOpen = false;
    unlockBodyScroll();
    logger.debug('basket closed');
  };

  /**
   * Переключить видимость корзины
   */
  const toggleBasket = (open?: boolean): void => {
    const newState = open === undefined ? !state.value.isBasketOpen : open;
    if (newState) {
      openBasket();
    } else {
      closeBasket();
    }
  };

  // Public API
  /**
   * Состав корзины уже относится к ВЫБРАННОМУ филиалу.
   *
   * Смена филиала переносит корзину асинхронно: позиции переотправляются на новый
   * филиал и перечитываются с сервера. Пока переезд идёт, состав в сторе ещё от
   * старого филиала — и всякий, кто в этот момент делает выводы о корзине, ошибётся.
   * Расчёт заказа так показывал ложное «товар недоступен», а механика подарков
   * не находила подарок под новым id и выдавала ЕЩЁ один: за каждое переключение
   * филиала количество подарка росло на единицу.
   */
  const isSettledForBranch = computed(() => {
    if (isMigratingBranch.value || state.value.isLoading) return false;

    const cartBranchId = state.value.selectedBranchId;
    const currentBranchId = storeInstitution.selectedBranch?.id;
    // Корзина ещё не привязана к филиалу (первая загрузка) — никому не мешаем.
    if (cartBranchId == null || currentBranchId == null) return true;

    return String(cartBranchId) === String(currentBranchId);
  });

  return {
    // State
    cart: computed(() => state.value.items),
    isBasketOpen: computed(() => state.value.isBasketOpen),
    selectedDish: computed(() => state.value.selectedDish),
    selectedBranchId: computed(() => state.value.selectedBranchId),
    isLoading: computed(() => state.value.isLoading),
    isSettledForBranch,
    preferredVariantId: computed(() => preferredVariantId.value),

    // Getters
    // Застрявшие позиции в суммы НЕ входят: в этом филиале их не приготовят,
    // и человек не должен видеть их в сумме к оплате.
    totalItems: computed(() =>
      state.value.items.reduce((sum, item) => sum + item.quantity, 0),
    ),
    totalPrice: computed(() =>
      state.value.items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    ),
    /** Позиции, недоступные в выбранном филиале (красные карточки в корзине) */
    strandedItems: computed(() => strandedItems.value),

    // Actions
    toggleBasket,
    setSelectedDish(dish: CartItem | null, variantId?: number | string | null) {
      logger.debug('setSelectedDish called', {
        dishId: (dish as any)?.id,
        dishName: (dish as any)?.name,
        dishType: (dish as any)?.type,
        variantId: variantId ?? null,
        variantIdType: typeof variantId,
      });
      state.value.selectedDish = dish;
      preferredVariantId.value = variantId ?? null;
      logger.debug('preferredVariantId set to', { variantId: preferredVariantId.value });
    },

    // Cart actions
    addToCart,
    addComboToCart,
    addBundleToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    openBasket,
    closeBasket,
    trackCartOpened,

    // Server sync
    loadCartFromServer,
    transferCartToServer,
    initializeCart,
    updateCartFromServerResponse,
  };
});
